import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Flame, Zap, Menu, X, ChevronDown, Camera, MessageCircle, Mic, Swords, Brain, LayoutDashboard, BookMarked, Settings, Sparkles, BookOpen, GraduationCap, ScanEye, NotebookPen, MessagesSquare, Compass } from "lucide-react";
import Footer from "@/components/Footer";
import NudgeBanner from "@/components/NudgeBanner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, primary: true },
  { to: "/today", label: "Today", icon: Sparkles, primary: true },
  { to: "/app", label: "LanguageLoop", icon: Camera },
  { to: "/lens", label: "FluentLens", icon: ScanEye, primary: true },
  { to: "/journal", label: "Journal", icon: NotebookPen },
  { to: "/friend", label: "Friend", icon: MessagesSquare, primary: true },
  { to: "/roommate", label: "Roommate", icon: MessageCircle },
  { to: "/accent", label: "AccentCoach", icon: Mic },
  { to: "/debate", label: "DebateGym", icon: Swords },
  { to: "/quiz", label: "QuizForge", icon: Brain },
  { to: "/story", label: "Story", icon: BookOpen },
  { to: "/vocab", label: "Vocab Bank", icon: BookMarked, primary: true },
  { to: "/discovery", label: "Discovery Lab", icon: Compass },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, dir } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [canTeach, setCanTeach] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("fluent_can_teach") === "1");
  useEffect(() => {
    if (!user) return;
    api.get("/teacher/is-staff").then((r) => {
      const ok = !!r.data?.can_teach;
      setCanTeach(ok);
      if (ok) sessionStorage.setItem("fluent_can_teach", "1");
      else sessionStorage.removeItem("fluent_can_teach");
    }).catch(() => {});
  }, [user]);
  const [eduPending, setEduPending] = useState(0);
  useEffect(() => {
    if (!user?.is_admin) return undefined;
    const refresh = () => api.get("/admin/education/summary").then((r) => setEduPending(r.data?.needs_owner ?? r.data?.pending ?? 0)).catch(() => {});
    refresh();
    window.addEventListener("edu-pending-changed", refresh);
    return () => window.removeEventListener("edu-pending-changed", refresh);
  }, [user, location.pathname]);
  const [hasClasses, setHasClasses] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("fluent_has_classes") === "1");
  useEffect(() => {
    if (!user) return;
    api.get("/my-classes").then((r) => {
      const ok = (r.data?.classes || []).length > 0;
      setHasClasses(ok);
      if (ok) sessionStorage.setItem("fluent_has_classes", "1"); else sessionStorage.removeItem("fluent_has_classes");
    }).catch(() => {});
  }, [user]);
  // School is an extra context: "My classes" lives under More, only for learners in an active class.
  let nav = hasClasses ? [...NAV, { to: "/my-classes", label: "MyClasses", icon: GraduationCap }] : NAV;
  if (canTeach) {
    nav = [...nav, { to: "/teacher", label: "Teacher", icon: GraduationCap }];
  }
  if (user?.is_admin) {  // server re-checks on every admin call; this only controls visibility
    nav = [...nav, { to: "/admin/education", label: "EducationAdmin", icon: GraduationCap, badge: eduPending }];
  }
  const isActive = (n) => location.pathname === n.to || (["/friend", "/vocab"].includes(n.to) && location.pathname.startsWith(`${n.to}/`));
  const moreActive = nav.find((n) => !n.primary && isActive(n));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 bg-white border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X /> : <Menu />}
            </button>
            <Link to="/dashboard" className="font-cabinet font-extrabold text-2xl whitespace-nowrap" dir="ltr" data-testid="header-brand">GetFluent<span className="text-coral">Loop</span></Link>
          </div>
          {/* Desktop: core destinations inline, the rest under "More" so the bar never outgrows the screen. */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 min-w-0 mx-2" data-testid="desktop-nav">
            {nav.filter((n) => n.primary).map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`px-2 xl:px-3 py-2 rounded-lg text-sm font-bold font-cabinet whitespace-nowrap transition-all ${isActive(n) ? "bg-ink text-white" : "hover:bg-paper"}`}>
                {t(`nav.${n.label.toLowerCase()}`)}
              </Link>
            ))}
            <DropdownMenu dir={dir}>
              <DropdownMenuTrigger data-testid="nav-more"
                className={`px-2 xl:px-3 py-2 rounded-lg text-sm font-bold font-cabinet whitespace-nowrap flex items-center gap-1 transition-all ${moreActive ? "bg-ink text-white" : "hover:bg-paper"}`}>
                <span className="max-w-[6.5rem] xl:max-w-none truncate">{moreActive ? t(`nav.${moreActive.label.toLowerCase()}`) : t("nav.more")}</span> <ChevronDown className="w-4 h-4 shrink-0" />
                {eduPending > 0 && user?.is_admin && <span className="w-2 h-2 rounded-full bg-coral" aria-label={`${eduPending} pending`} data-testid="nav-more-pending-dot" />}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-ink rounded-xl shadow-hard bg-white">
                {nav.filter((n) => !n.primary).map((n) => (
                  <DropdownMenuItem key={n.to} onSelect={() => navigate(n.to)} data-testid={`nav-${n.label.toLowerCase()}`}
                    className={`gap-3 font-bold font-cabinet cursor-pointer ${isActive(n) ? "bg-ink text-white" : ""}`}>
                    <n.icon className="w-4 h-4" /> {t(`nav.${n.label.toLowerCase()}`)}
                    {n.badge > 0 && <span className="nb-tag bg-coral text-white text-[10px] py-0 px-1.5" data-testid="nav-educationadmin-badge">{n.badge}</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:flex items-center gap-1 nb-tag bg-butter" data-testid="nav-streak">
              <Flame className="w-4 h-4" /> {user?.streak_count || 0}
            </div>
            <div className="hidden sm:flex items-center gap-1 nb-tag bg-mint" data-testid="nav-xp">
              <Zap className="w-4 h-4" /> {user?.xp || 0}
            </div>
            <Link to="/settings" className="w-9 h-9 rounded-full border-2 border-ink bg-lavender flex items-center justify-center font-cabinet font-bold" data-testid="nav-avatar">
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </Link>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t-2 border-ink bg-white px-4 py-3 space-y-1">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg font-bold hover:bg-paper">
                <n.icon className="w-5 h-5" /> {t(`nav.${n.label.toLowerCase()}`)}
                {n.badge > 0 && <span className="nb-tag bg-coral text-white text-[10px] py-0 px-1.5" data-testid="mobile-nav-educationadmin-badge">{n.badge}</span>}
              </Link>
            ))}
            <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-lg font-bold hover:bg-paper">
              <Settings className="w-5 h-5" /> {t("common.settings")}
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} className="w-full text-start px-3 py-2 rounded-lg font-bold text-coral">{t("common.logout")}</button>
          </div>
        )}
      </header>
      <NudgeBanner />
      {user?.subscription_status === "past_due" && location.pathname !== "/billing" && (
        <Link to="/billing" className="block bg-peach border-b-2 border-ink text-center text-xs font-bold py-1.5 hover:underline" data-testid="billing-pastdue-notice">
          Your last payment didn't go through — update your payment details to keep Pro.
        </Link>
      )}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
